import React, { useEffect, useRef } from "react";

function AboutUs() {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === imageRef.current) {
              entry.target.classList.add("animate-image");
            }
            if (entry.target === contentRef.current) {
              entry.target.classList.add("animate-content");
            }
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -50px 0px" }
    );

    if (imageRef.current) observer.observe(imageRef.current);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>
        {`
          .about-section {
            position: relative;
            overflow: hidden;
            background: linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%);
          }

          .about-section::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -20%;
            width: 500px;
            height: 500px;
            background: radial-gradient(circle, rgba(32, 117, 207, 0.05) 0%, rgba(32, 117, 207, 0) 70%);
            border-radius: 50%;
            pointer-events: none;
          }

          .about-section::after {
            content: '';
            position: absolute;
            bottom: -30%;
            left: -10%;
            width: 400px;
            height: 400px;
            background: radial-gradient(circle, rgba(32, 117, 207, 0.04) 0%, rgba(32, 117, 207, 0) 70%);
            border-radius: 50%;
            pointer-events: none;
          }

          .about-image-wrapper {
            position: relative;
            opacity: 0;
            transform: translateX(-50px);
            transition: all 0.8s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          }

          .about-image-wrapper.animate-image {
            opacity: 1;
            transform: translateX(0);
          }

          .about-image {
            width: 100%;
            border-radius: 30px;
            box-shadow: 0 25px 50px -12px rgba(32, 117, 207, 0.25);
            transition: all 0.4s ease;
          }

          .about-image:hover {
            transform: scale(1.02);
            box-shadow: 0 30px 60px -12px rgba(32, 117, 207, 0.35);
          }

          .experience-badge {
            position: absolute;
            bottom: 30px;
            left: 30px;
            background: linear-gradient(135deg, #2075cf, #155fb2);
            border-radius: 20px;
            padding: 15px 25px;
            text-align: center;
            color: white;
            box-shadow: 0 10px 25px rgba(32, 117, 207, 0.3);
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 10px 25px rgba(32, 117, 207, 0.3);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 15px 35px rgba(32, 117, 207, 0.5);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 10px 25px rgba(32, 117, 207, 0.3);
            }
          }

          .experience-badge h3 {
            font-size: 28px;
            font-weight: 800;
            margin: 0;
            line-height: 1.2;
          }

          .experience-badge p {
            font-size: 12px;
            margin: 0;
            opacity: 0.9;
          }

          .about-content-wrapper {
            opacity: 0;
            transform: translateX(50px);
            transition: all 0.8s cubic-bezier(0.2, 0.9, 0.4, 1.1) 0.2s;
          }

          .about-content-wrapper.animate-content {
            opacity: 1;
            transform: translateX(0);
          }

          .about-badge {
            display: inline-block;
            background: linear-gradient(135deg, #2075cf15, #155fb215);
            color: #2075cf;
            padding: 6px 16px;
            border-radius: 50px;
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 1px;
            margin-bottom: 20px;
            border: 1px solid rgba(32, 117, 207, 0.2);
          }

          .about-title {
            font-size: 48px;
            font-weight: 800;
            margin-bottom: 20px;
            background: linear-gradient(135deg, #1a1a2e, #2075cf);
            background-clip: text;
            -webkit-background-clip: text;
            color: transparent;
          }

          .about-text {
            font-size: 16px;
            line-height: 1.8;
            color: #4a5568;
            margin-bottom: 20px;
            text-align: justify;
          }

          .stats-row {
            display: flex;
            gap: 30px;
            margin: 30px 0;
            flex-wrap: wrap;
          }

          .stat-item {
            flex: 1;
            min-width: 100px;
            text-align: center;
            padding: 15px;
            background: white;
            border-radius: 16px;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
          }

          .stat-item:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px rgba(32, 117, 207, 0.1);
          }

          .stat-number {
            font-size: 32px;
            font-weight: 800;
            background: linear-gradient(135deg, #2075cf, #155fb2);
            background-clip: text;
            -webkit-background-clip: text;
            color: transparent;
          }

          .stat-label {
            font-size: 14px;
            color: #666;
            margin-top: 5px;
          }

          .about-btn {
            background: linear-gradient(135deg, #2075cf, #155fb2);
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            padding: 12px 32px;
            color: white;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
            box-shadow: 0 5px 15px rgba(32, 117, 207, 0.3);
          }

          .about-btn::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%);
            transition: width 0.6s, height 0.6s;
          }

          .about-btn:hover::before {
            width: 300px;
            height: 300px;
          }

          .about-btn:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 25px rgba(32, 117, 207, 0.4);
          }

          .floating-shape {
            position: absolute;
            background: linear-gradient(135deg, #2075cf20, #155fb210);
            border-radius: 30px;
            pointer-events: none;
            z-index: 0;
          }

          .shape-1 {
            width: 150px;
            height: 150px;
            top: 10%;
            right: 5%;
            border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
            animation: floatShape 8s ease-in-out infinite;
          }

          .shape-2 {
            width: 100px;
            height: 100px;
            bottom: 15%;
            left: -30px;
            border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
            animation: floatShape 6s ease-in-out infinite reverse;
          }

          @keyframes floatShape {
            0% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(10deg);
            }
            100% {
              transform: translateY(0px) rotate(0deg);
            }
          }

          @media (max-width: 768px) {
            .about-title {
              font-size: 32px;
            }
            .stats-row {
              gap: 15px;
            }
            .stat-item {
              padding: 10px;
            }
            .stat-number {
              font-size: 24px;
            }
            .experience-badge {
              bottom: 15px;
              left: 15px;
              padding: 10px 18px;
            }
            .experience-badge h3 {
              font-size: 20px;
            }
          }
        `}
      </style>

      <div className="about-section py-5 position-relative" ref={sectionRef}>
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>

        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-md-6">
              <div className="about-image-wrapper" ref={imageRef}>
                <img
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
                  alt="About us - Team collaborating"
                  className="about-image"
                />
                <div className="experience-badge" style={{ color:'#ffff'}}>
                  <h3 style={{color:'#ffff'}}>8+</h3>
                  <p style={{color:'#ffff'}}>Years of<br />Experience</p>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="about-content-wrapper" ref={contentRef}>
                <span className="about-badge">
                  <i className="fa-regular fa-building me-2"></i> Who We Are
                </span>
                <h1 className="about-title">
                  Creating Digital<br />
                  <span style={{ color: "#2075cf" }}>Solutions That Matter</span>
                </h1>

                <p className="about-text">
                 DigiOpen Services incorporated in the year 2018 is brainchild of DigiOpen Technologies team of industry experts. The organization is led by industry professionals with over 15 years of expertise in Enterprise Resource Planning (ERP) solutions and a proven track record of successfully delivering more than 30 ERP implementations across diverse industries.
                </p>
<p className="about-text">Traditionally, ERP solutions have been perceived as complex and expensive investments. DigiOpen Services is redefining this perception by delivering robust, feature-rich ERP solutions that are both affordable and accessible to businesses of all sizes. Our mission is to empower organizations with a comprehensive, integrated platform that streamlines and manages end-to-end business processes efficiently. By combining advanced functionality with cost-effective deployment, we make enterprise-grade ERP technology attainable for every business seeking operational excellence and sustainable growth.</p>
           
                <div className="stats-row">
                  <div className="stat-item">
                    <div className="stat-number">8+</div>
                    <div className="stat-label">Years Experience</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">30+</div>
                    <div className="stat-label">ERP Implementations</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">100%</div>
                    <div className="stat-label">Client Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AboutUs;